import PropTypes from 'prop-types';
import {
	useState, useEffect, useRef,
} from 'react';
import { useRouter } from 'next/router';
import camelCase from 'camelcase';
import PostBlockGrid from './PostBlockGrid';
import { TAG_PAGES_QUERY } from '../Tags/TagPagesQuery';
import { RECIPE_FILTERS_DATA } from '../RecipeFilter/RecipeFiltersData';
import { initializeApollo } from '../../utils/apolloClient';
import { useScrollRestoration, restoreScrollPos, resetScrollPaths } from '../../utils/useScrollRestoration';
import { addTrailingSlash, getSlugFromPath } from '../../utils/utility';
import { handleFilterInteraction } from '../../utils/analytics';

/**
 * This component generates a post-block grid from the results of the custom tag query.
 * The most important prop is the options object, which is very similar to the
 * variables object used in the apollo/graphql query.  It holds the same
 * property/value combinations **except** 'paged' is 'page' here.
 *
 * @param {initialPosts} array initial array of posts
 * @param {initialFoundPosts} number total number of posts for the query
 * @param {options} object containing options for the tag query
 * @param {setOptions} function that changes the options for the query
 * @param {noResultsMessge} Component that contains message when no results are returned
 * @param {className} string Classname of div when rendered
 * @param {passQueryVar} boolean Set to true if the query variables should be passed along to the tag links
 * @returns PostsBlockGridWithQuery
 */
export default function PostsBlockGridWithQuery({
	options, setOptions, noResultsMessage = null, className = 'tag-posts-block', initialPosts = [], initialFoundPosts = 0, passQueryVar = false,
}) {
	const [posts, setPosts] = useState(initialPosts);
	const [isLoading, setIsLoading] = useState(false);
	const [foundPosts, setFoundPosts] = useState(initialFoundPosts);
	const isLoadMore = useRef(false);
	const isFirstUpdate = useRef(true);
	const router = useRouter();
	const adConfig = {
		loadGoogleAds: true,
		loadMagniteAds: false,
		adName: (className) ? `${className}-ad` : null,
	};

	useScrollRestoration(router);

	const getLoadMoreLink = () => {
		const params = new URLSearchParams((typeof window !== 'undefined') ? window.location.search : router.query);
		const currentPage = parseInt(params.get('page'), 10) || 1;
		params.set('page', currentPage + 1);
		return `${addTrailingSlash(router.pathname)}?${params.toString()}`;
	};

	// Adobe analytics data
	const adobeAnalyticsData = {
		name: `${getSlugFromPath(router.pathname)}:load-more`,
	};

	const handleLoadMore = (e) => {
		isLoadMore.current = true;
		e.preventDefault();
		e.stopPropagation();
		handleFilterInteraction(null, adobeAnalyticsData.name);
		setOptions((prevOptions) => {
			const newPage = prevOptions.page + 1;
			return {
				...options,
				page: newPage,
			};
		});
	};

	const getQueryVariables = () => {
		let paged = 1;
		let { limit = 12 } = options;
		if (isLoadMore.current === true) {
			paged = options.page;
		} else if (isFirstUpdate.current === true) {
			limit *= options.page;
		}
		return ({
			...options,
			paged,
			limit,
		});
	};

	useEffect(() => {
		// When user is routed to this page (Eg. from nav menu),
		// reload the page to get server-side data
		if (isFirstUpdate.current === false && isLoadMore.current === false) {
			router.reload();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.query.page]);

	useEffect(() => {
		if (isFirstUpdate.current === true) return; // Data was fetched server-side
		let url = window.location.href;
		const params = new URLSearchParams(window.location.search);
		// Retain non-filter search params
		Object.keys(RECIPE_FILTERS_DATA).forEach((filterKey) => {
			const filterName = camelCase(RECIPE_FILTERS_DATA[filterKey].title);
			params.delete(filterName);
		});

		// Create and add new params for new filters
		Object.keys(RECIPE_FILTERS_DATA).forEach((key) => {
			const filterName = camelCase(RECIPE_FILTERS_DATA[key].title);
			if (options[filterName]) {
				const filters = options[filterName].split(',');
				filters.forEach((item) => {
					params.append(filterName, item);
				});
			}
		});

		// Update the url in address bar
		if (isLoadMore.current === true) {
			url = getLoadMoreLink();
		} else if (isFirstUpdate.current === false) {
			// Reset the page number
			params.delete('page');
			const queryString = (params.toString()) ? `?${params.toString()}` : '';
			url = `${window.location.href.split('?')[0]}${queryString}`;
		}
		router.replace(url, url, { shallow: true });

		/*
		 * Fetch the filtered posts via graphql tag pages query
		 */
		const fetchFiltersData = async () => {
			setIsLoading(true);

			const apolloClient = initializeApollo();
			const response = await apolloClient.query({
				query: TAG_PAGES_QUERY,
				variables: getQueryVariables(),
			});

			if (response) {
				const { data } = response;
				const fetchedPosts = (data && data?.tagPages.found_posts) ? data?.tagPages?.posts : [];
				setFoundPosts(data.tagPages.found_posts);
				setIsLoading(false);
				if (isLoadMore.current === true) {
					setPosts([...posts, ...fetchedPosts]);
					isLoadMore.current = false;
				} else {
					setPosts(fetchedPosts);
				}
			}
		};

		fetchFiltersData();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options]);

	useEffect(() => {
		if (isFirstUpdate.current === true) {
			isFirstUpdate.current = false;
			if (isLoadMore.current === false) {
				// Delay a bit to let the ads load
				setTimeout(() => {
					// Restore scroll position with ads loaded
					restoreScrollPos(router.asPath);
					// Remove all previously saved scroll positions
					resetScrollPaths(`${router.pathname}/`);
				}, 800);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderRecipeBlocks = () => {
		if (isLoading || posts.length > 0) {
			if (isLoadMore.current === false) {
				return (
					<PostBlockGrid posts={posts} adConfig={adConfig} passQueryVar={passQueryVar} isLoader={isLoading} />
				);
			}
			return (
				<PostBlockGrid posts={posts} adConfig={adConfig} passQueryVar={passQueryVar} isLoader={false} />
			);
		}
		return noResultsMessage;
	};

	return (
		<div className={className}>
			{renderRecipeBlocks()}
			{(isLoading && isLoadMore.current) && <PostBlockGrid className="loader-grid" posts={[]} isLoader />}
			<div className={`${className}-footer`}>
				{
					((posts.length < foundPosts) && !isLoading) ? (
						<a
							onClick={(e) => handleLoadMore(e)}
							href={getLoadMoreLink()}
							className={`${className}-loadmore-button`}
						>
							Load More
						</a>
					) : null
				}
			</div>
		</div>
	);
}

PostsBlockGridWithQuery.propTypes = {
	options: PropTypes.object.isRequired,
	setOptions: PropTypes.func.isRequired,
	noResultsMessage: PropTypes.object,
	className: PropTypes.string,
	initialPosts: PropTypes.array,
	initialFoundPosts: PropTypes.number,
	passQueryVar: PropTypes.bool,
};
